import {
  DvFilterItem,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  output,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { VehiclesMapService } from '@pages/vehicles-page/services/vehicles-map.service';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { AutoZoomFitParams, MapInput } from '@features/map/interfaces';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  interval,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { VehiclesTabsService } from '@pages/vehicles-page/services/vehicles-tabs.service';
import { CustomVehicleMapSettings, VehicleDto } from './interfaces';
import { VehiclesGridService } from './vehicles-grid.service';
import { environment } from '@env/environment';
import { MapSetting } from '@features/map/constants/constants';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import { FormGroup } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { MapId } from '@features/map/constants/map-id.enum';
import { CommonQuickFiltersService } from '@shared/services/grid/common-quick-filters.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-vehicles-grid',
  templateUrl: './vehicles-grid.component.html',
  styleUrls: ['./vehicles-grid.component.scss'],
  providers: [GridOptionsService, VehiclesMapService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehiclesGridComponent implements OnInit {
  private vehicleGridService = inject(VehiclesGridService);
  private gridOptionsService = inject(GridOptionsService);
  private vehiclesMapService = inject(VehiclesMapService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private readonly vehiclesTabsService = inject(VehiclesTabsService);
  private commonQuickFiltersService = inject(CommonQuickFiltersService);
  private api = inject(RestApiService);
  private destroyRef = inject(DestroyRef);

  gridFiltersCleared = output<void>();

  actions = signal<DvToolbarAction[]>([]);
  gridOptions: GridOptions;
  quickFilters: DvFilterItem[] = [];
  customSettingsForm: FormGroup<FormOf<CustomVehicleMapSettings>>;
  customSettingsElements: DvDynamicFormElement[];
  gridApi: GridApi<VehicleDto>;

  readonly mapSetting = MapSetting.VehiclesGridMap;

  @ViewChild('menuPanelTemplate')
  menuPanelContent: TemplateRef<TimedMarkerMapMenuPanelComponent>;

  private selectedNode?: IRowNode<VehicleDto>;
  private selectedVehicleMarker$ = new BehaviorSubject<MapInput[]>([]);
  private canDelete: boolean;
  private canSeeMap: boolean;
  private activeQuickFilter: DvFilterItem | undefined;
  private refreshSubscription: Subscription | undefined;
  private autoZoomFit$ = new BehaviorSubject<AutoZoomFitParams>({
    doAutoFit: false,
  });

  ngOnInit(): void {
    this.handleAccessRestrictions();

    this.gridOptions = {
      ...this.gridOptionsService.clientSide(
        this.vehicleGridService.getColDefs(),
        Entity.Vehicle
      ),
      rowClassRules: {
        'vt-deleted-row': params =>
          !params.data?.ShowRecord && !params.node.group,
      },
    };
    this.customSettingsElements =
      this.vehiclesMapService.customVehicleSettingElements;
    this.customSettingsForm = DvFormUtils.createFormGroup(
      this.customSettingsElements
    );
    this.actions.set(
      this.vehicleGridService.getToolbarActions(
        this.showVehicleOnMap,
        this.deleteSelectedVehicle
      )
    );
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  rowDataUpdated({ api }: RowDataUpdatedEvent): void {
    this.toggleGridToolbarButtons(api);
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.toggleGridToolbarButtons(api);
  }

  filtersCleared(): void {
    this.gridFiltersCleared.emit();
  }

  onQuickFilterChange(activeFilter: DvFilterItem): void {
    this.activeQuickFilter = activeFilter;
    setTimeout(() => {
      this.gridApi.getDisplayedRowAtIndex(0)?.setSelected(true, true);
    });
  }

  onAutoRefreshClicked(isAutoRefreshOn: boolean): void {
    if (isAutoRefreshOn) {
      this.refreshSubscription = interval(environment.mapRefreshInterval)
        .pipe(
          switchMap(() =>
            this.getSingleVehicleLatestData(this.selectedNode!.data!).pipe(
              tap(resp => {
                this.selectedVehicleMarker$.next(
                  this.vehiclesMapService.getMapMarkers([resp as VehicleDto])
                );
              })
            )
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.refreshSubscription?.unsubscribe();
    }
  }

  onAutoZoomFitClicked(isAutoZoomOn: boolean): void {
    this.autoZoomFit$.next({ doAutoFit: isAutoZoomOn });
  }

  onCustomSettingsChanged(settings: CustomVehicleMapSettings): void {
    this.selectedVehicleMarker$.next(
      this.vehiclesMapService.getMapMarkers(
        [this.selectedNode!.data!],
        settings
      )
    );
  }

  private showVehicleOnMap = (): void => {
    if (!this.selectedNode?.data) return;

    this.getSingleVehicleLatestData(this.selectedNode.data)
      .pipe(
        switchMap(data => {
          this.selectedVehicleMarker$.next(
            this.vehiclesMapService.getMapMarkers([data])
          );

          return this.vehiclesMapService.showVehiclesOnMap(
            MapId.SingleVehicleMap,
            this.selectedVehicleMarker$,
            this.menuPanelContent,
            this.autoZoomFit$
          ).closed;
        }),
        take(1)
      )
      .subscribe(() => this.refreshSubscription?.unsubscribe());
  };

  private getSingleVehicleLatestData(
    selectedNodeData: VehicleDto
  ): Observable<VehicleDto> {
    return this.api.getInfo<VehicleDto>(
      Entity.Vehicle,
      String(selectedNodeData.VehicleID),
      this.vehicleGridService
        .getColDefs()
        .map((colDef: ColDef) => colDef.field!)
        .filter(field => field)
    );
  }

  private handleAccessRestrictions(): void {
    this.permissionsService
      .getUserRolesAndPermissions()
      .pipe(take(1))
      .subscribe(data => {
        if (data.Permissions.includes(RolesAndPermissions.VehicleDelete)) {
          this.canDelete = true;
        }
        if (data.Permissions.includes(RolesAndPermissions.VehicleShowInMap)) {
          this.canSeeMap = true;
        }
        if (data.Permissions.includes(RolesAndPermissions.VehicleShowDeleted)) {
          this.quickFilters = this.commonQuickFiltersService.getQuickFilters();
          this.activeQuickFilter = this.quickFilters[1];
        }
      });
  }

  private toggleShowInMapToolbarBtn(): void {
    const nodeData = this.selectedNode?.data;
    const coordsDefined = !!nodeData?.LastGPSLat && !!nodeData.LastGPSLon;

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'map_marker_move') {
          return {
            ...action,
            disabled: !this.canSeeMap || !this.selectedNode || !coordsDefined,
          };
        }
        return action;
      })
    );
  }

  private toggleGridToolbarButtons(api: GridApi): void {
    this.selectedNode = api.getSelectedNodes()[0] as IRowNode | undefined;

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled:
              !this.canDelete ||
              !this.selectedNode ||
              !this.selectedNode.data?.ShowRecord,
          };
        }
        if (action.icon === 'edit') {
          return { ...action, disabled: !this.selectedNode };
        }
        return action;
      })
    );
    this.toggleShowInMapToolbarBtn();

    setTimeout(() => {
      this.vehiclesTabsService.selectionChanged$.next();
    });
  }

  private deleteSelectedVehicle = (event: DvToolbarActionEvent): void => {
    return this.commonQuickFiltersService.deleteEntity(
      event,
      this.quickFilters,
      this.activeQuickFilter,
      Entity.Vehicle,
      'Vehicle.DeleteRowText'
    );
  };
}
